import { PageSections, type SectionProps, type SectionRenderers } from "@/components/cms/PageSections";
import { EnquiryForm } from "@/components/forms/EnquiryForm";
import { PageHeader } from "@/components/ui/PageHeader";
import { Reveal } from "@/components/ui/Reveal";
import { Section, SectionHeading } from "@/components/ui/Section";
import { useSettings } from "@/content-context";
import { buildMetadata } from "@/lib/seo";
import { pageLoader } from "@/page-data";

/**
 * Contact.
 *
 * The address, phone, email, social channels and registration numbers come from
 * the organisation profile, so they are the same here, in the footer and in the
 * structured data, and a detail appears only once it is confirmed there. They
 * used to be read from the committed snapshot, which meant a corrected phone
 * number reached the footer and not this page.
 */

export const loader = pageLoader("contact");

export const meta = () =>
  buildMetadata({
    title: "Contact",
    description:
      "Contact Himshikhara Socio-Cultural Society (HISCUS Nepal) about partnership, funding, CSR, volunteering, Charity Trek, local collaboration, or media and research.",
    path: "/contact",
  });

function DetailsSection({ section }: SectionProps) {
  const settings = useSettings();
  const { address, phone, email } = settings;

  return (
    <Section>
      <div className="grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] lg:gap-16">
        <Reveal>
          <SectionHeading eyebrow={section.eyebrow} title={section.title || "Where to find us"} />

          <div className="mt-8 space-y-8">
            {/*
              An unconfirmed street address is simply not published. There
              used to be a line here explaining that it was pending, which
              told a visitor about our process rather than about HISCUS.
            */}
            {address.confirmed && address.value ? (
              <div>
                <h3 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">Address</h3>
                <address className="mt-3 not-italic text-ink">{address.value}</address>
              </div>
            ) : null}

            <div>
              <h3 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">Phone and email</h3>
              {phone.confirmed && phone.value ? (
                <p className="mt-3">
                  <a
                    href={`tel:${phone.value.replace(/\s/g, "")}`}
                    className="inline-flex min-h-11 items-center font-semibold text-navy underline underline-offset-4"
                  >
                    {phone.value}
                  </a>
                </p>
              ) : null}
              {email.confirmed && email.value ? (
                <p>
                  <a
                    href={`mailto:${email.value}`}
                    className="inline-flex min-h-11 items-center font-semibold text-navy underline underline-offset-4"
                  >
                    {email.value}
                  </a>
                </p>
              ) : section.texts?.noEmail ? (
                <p className="mt-3 text-muted">{section.texts.noEmail}</p>
              ) : null}
            </div>

            {settings.socialLinks.length ? (
              <div>
                <h3 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">Social channels</h3>
                <ul className="mt-3 space-y-1">
                  {settings.socialLinks.map((social) => (
                    <li key={social.href}>
                      <a
                        href={social.href}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="inline-flex min-h-11 items-center font-semibold text-navy underline underline-offset-4"
                      >
                        {social.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {settings.registrations.length ? (
              <div className="rounded-[16px] border border-line bg-mist p-6">
                <h3 className="text-[0.8125rem] font-bold uppercase tracking-[0.14em] text-blue">Registration</h3>
                <dl className="mt-4 space-y-3">
                  {settings.registrations.map((reg) => (
                    <div
                      key={reg.value}
                      className="flex flex-wrap justify-between gap-x-6 gap-y-1 border-b border-line pb-3 last:border-0 last:pb-0"
                    >
                      <dt className="text-[0.9375rem] text-muted">{reg.label}</dt>
                      <dd className="font-semibold text-ink">{reg.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}
          </div>
        </Reveal>

        <Reveal>
          <EnquiryForm heading={section.texts?.formHeading || "Send us a message"} defaultInterest="general" />
        </Reveal>
      </div>
    </Section>
  );
}

export const contactSections: SectionRenderers = {
  "contact.details": DetailsSection,
};

export function ContactPage() {
  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Get in touch with HISCUS"
        lead="One form for every kind of enquiry. Choose what it is about and it reaches the right person."
        trail={[
          { name: "Home", href: "/" },
          { name: "Contact", href: "/contact" },
        ]}
      />
      <PageSections renderers={contactSections} />
    </>
  );
}
